import React from 'react';
import { connect } from 'react-redux';
import { PieChart, Pie, Tooltip, Cell } from 'recharts';
import styled from 'styled-components';
import { uniq, intersection, flatten } from 'lodash';
import ContainerDimensions from 'react-container-dimensions';
import Settings from './Settings';
import Loader from '../../../general/Loader';
import DashboardRegistrationManager from '../../DashboardRegistrationManager';
import {
  availabilityCodeToTasksMap,
  loading as availabilityRequirementSchedulesLoading
} from '../../../../state/modules/availability-requirement-schedules/selectors';

const PieChartContainer = styled.div`
  width: 100%;
  height: 90%;
  position: relative;
`;

class ResponseLevelPieChart extends React.Component {
  constructor(props) {
    super(props);

    props.dashboardAPIManager.fetchAvailabilityCodes();
    props.dashboardAPIManager.fetchTasks();

    this.dashboardRegistrationManager = new DashboardRegistrationManager();
    this.state = { groupIds: props.groupIds };
    // make the store listen for updates about availability requirements
    // schedules for the selected group ids or the default group id
    if (this.props.groupIds.length === 0) {
      this.dashboardRegistrationManager.registerForAvailabilityRequirementScheduleUpdates({
        groupId: props.groupId
      });
    } else
      this.props.groupIds.map(id => {
        this.dashboardRegistrationManager.registerForAvailabilityRequirementScheduleUpdates({
          groupId: id
        });
      });
  }

  static SettingsContent = Settings;
  static defaultSettings = { groupIds: [] };

  // Get all the relevant group ids, including descendants of chosen groups
  // remove duplicates
  allRelevantGroupIds = () => {
    const groups = this.props.groups.byId;
    // If no group has been chosen, return current group and descendants
    if (this.props.groupIds.length === 0) return [this.props.groupId, ...groups[this.props.groupId].descendant_ids];
    // return all descendant ids from the chosen groups
    return uniq(
      flatten(
        this.props.groupIds.map(id => {
          if (!groups[id]) return;
          return [id, ...groups[id].descendant_ids];
        })
      )
    );
  };

  chartData = groupIds => {
    return Object.entries(this.props.availabilityCodeToTasksMap).map(([_codeID, info]) => {
      if (!info) return {};
      const { availabilityCode, tasks } = info;
      // Filter out tasks that are not relevant to the chosen groups
      // Filter tasks by the relevant group ids
      const filteredTasks = tasks.filter(task => intersection(task.group_ids, groupIds).length > 0);

      const value = filteredTasks.length;
      const label = availabilityCode.name;
      const color = availabilityCode.html_color;

      return {
        value,
        color,
        // highlight: color,
        name: label
      };
    });
  };

  render() {
    if (
      this.props.availabilityRequirementSchedulesLoading ||
      !this.props.groups.lastFetch ||
      !this.props.tasks.lastFetch
    ) {
      return <Loader />;
    }
    // Get the groups that are being displayed in the piechart, this allows generating
    // the banner indicating the groups.
    const defaultGroup = this.props.groups.byId[this.props.groupId];
    const displayedGroups = this.props.groupIds.map(id => this.props.groups.byId[id]).filter(Boolean);
    const relevantGroups = this.allRelevantGroupIds();

    return (
      <React.Fragment>
        {displayedGroups.length > 0 ? (
          displayedGroups.map((g, index) => (
            <span key={index}>
              {index > 0 && ', '}
              {g.name}
            </span>
          ))
        ) : (
          <span>{defaultGroup.name}</span>
        )}
        <PieChartContainer>
          <ContainerDimensions>
            {({ width, height }) => (
              <PieChart width={width} height={height}>
                <Pie dataKey="value" isAnimationActive={false} data={this.chartData(relevantGroups)} label>
                  {this.chartData(relevantGroups).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            )}
          </ContainerDimensions>
        </PieChartContainer>
      </React.Fragment>
    );
  }
}

const mapStateToProps = state => ({
  groups: state.groups,
  tasks: state.tasks,
  availabilityRequirementSchedulesLoading: availabilityRequirementSchedulesLoading(state),
  availabilityCodeToTasksMap: availabilityCodeToTasksMap(state)
});

export default connect(mapStateToProps)(ResponseLevelPieChart);
